import { useEffect, useRef, useState } from 'react';
import { setPosStock, posReserveEnabled } from '../utils/posUtil';
import { useConfig } from '../context/ConfigContext';
import '../css/Pos.css';

/**
 * One product's stock count, editable where it stands.
 *
 * A STOCKTAKE, NOT A DELIVERY. What is typed here replaces the count —
 * "there are 18 on the shelf" — which is the claim someone counting stock can
 * actually make correctly. Recording an arrival ("20 came in") is a different
 * claim and belongs in Add stock, which adds to whatever the database holds at
 * the moment it commits. setPosStock records this as *Stock corrected* on the
 * audit trail, with the figure it replaced.
 *
 * SAVES ON BLUR OR ENTER, NOT ON EVERY KEYSTROKE. Typing "18" over "5" passes
 * through "1", and a per-keystroke save would write that to the shelf and log
 * it — a trail full of counts nobody ever meant.
 *
 * NOTHING IS WRITTEN WHEN THE FIGURE HAS NOT CHANGED. Tabbing across a row of
 * cells would otherwise log a correction for every product it passed, each one
 * claiming a change that never happened.
 *
 * @param {object} props
 * @param {string} props.clientId
 * @param {object} props.product - needs `id`, `name` and `supplies`
 * @param {string} [props.actor] - staff address, for the trail
 * @param {boolean} [props.disabled] - true without the "add stocks" permission,
 *   which renders the count as plain text instead of a field
 * @param {string} [props.tone] - 'out' | 'low' | 'ok', for the colour
 * @param {(error: Error) => void} [props.onError]
 * @returns {JSX.Element}
 */
function PosStockCell({ clientId, product, actor = '', disabled = false, tone = 'ok', onError }) {
  // With a reserve, raising the count here takes the difference out of it —
  // and is refused if the reserve is short (see setPosStock).
  const { config } = useConfig();
  const fromReserve = posReserveEnabled(config);
  const stored = Math.max(0, Math.floor(Number(product?.supplies) || 0));
  const [draft, setDraft] = useState(String(stored));
  const [saving, setSaving] = useState(false);
  // Guards the blur that Enter causes: without it, pressing Enter saves and
  // then blurring saves again.
  const committing = useRef(false);

  // Follow the live figure whenever it moves underneath — a sale on the till,
  // or a delivery recorded in the product modal. Skipped while this cell is
  // mid-save so the value being written does not flicker back to the old one.
  useEffect(() => {
    if (!saving) setDraft(String(stored));
  }, [stored, saving]);

  /**
   * Writes the typed count, or puts the field back if it cannot be used.
   * @returns {Promise<void>}
   */
  const commit = async () => {
    if (committing.current) return;
    const typed = draft.trim();

    // Unchanged, or emptied and abandoned: leave the shelf alone. An empty
    // field is a half-finished edit, not an instruction to zero the stock —
    // Number('') is 0, and treating it as one would wipe the count.
    if (typed === '' || typed === String(stored)) {
      setDraft(String(stored));
      return;
    }

    committing.current = true;
    setSaving(true);
    try {
      const after = await setPosStock(clientId, product, typed, actor, { fromReserve });
      setDraft(String(after));
    } catch (error) {
      // Back to what is actually stored, so the field never shows a figure the
      // database refused.
      setDraft(String(stored));
      if (onError) onError(error);
    } finally {
      setSaving(false);
      committing.current = false;
    }
  };

  if (disabled) {
    return <span className={`pos-stock-plain ${tone}`}>{stored}</span>;
  }

  return (
    <span className={`pos-stock-cell${saving ? ' saving' : ''}`}>
      <input
        type="number"
        min="0"
        step="1"
        inputMode="numeric"
        className={`pos-stock-input ${tone}`}
        value={draft}
        disabled={saving}
        aria-label={`Stock for ${product?.name || 'this product'}`}
        onChange={(event) => setDraft(event.target.value)}
        onFocus={(event) => event.target.select()}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') { event.preventDefault(); commit(); event.target.blur(); }
          // Escape abandons the edit rather than writing a half-typed figure.
          if (event.key === 'Escape') { setDraft(String(stored)); event.target.blur(); }
        }}
      />
      {/* A spinner rather than a disabled-looking field, so a slow write reads
          as "saving" instead of "broken". */}
      {saving && <span className="pos-stock-spin" aria-hidden="true" />}
    </span>
  );
}

export default PosStockCell;
